import React, { useEffect, useRef, useState } from "react";

import type { RootState } from "store";
import { useSelector } from "react-redux";
import { createChange } from "create_change";
import { AnimatePresence, Reorder } from "framer-motion";
import { ListItem } from "./list_item";
import { ReorderableListItem } from "./reorderable_list_item";
import { Item, Category, Reorderable, isCategory } from "types";

export const Categories = () => {
  const checklist = useSelector((state: RootState) => state.checklist.value);
  const categories = checklist.categories;
  const draggedListItem = useRef<Reorderable>();
  const categoriesAndItemsFromChecklist =
    categories.length == 1 && categories[0].name == ""
      ? categories[0].items
      : categories.flatMap((category) => [category, ...category.items]);
  const [categoriesAndItems, setCategoriesAndItems] = useState(
    categoriesAndItemsFromChecklist,
  );
  const [isDraggingCategory, setIsDraggingCategory] = useState(false);

  useEffect(() => {
    setCategoriesAndItems(
      isDraggingCategory ? categories : categoriesAndItemsFromChecklist,
    );
  }, [categories, isDraggingCategory]);

  const onReorder = (listItems: Reorderable[]) => {
    setCategoriesAndItems(listItems);
  };

  const moveReordeable = () => {
    const droppedItem = draggedListItem.current!;

    if (isCategory(droppedItem)) {
      moveCategory(categoriesAndItems, droppedItem);
    } else {
      moveItem(categoriesAndItems, droppedItem);
    }

    draggedListItem.current = undefined;
    setIsDraggingCategory(false);
    setCategoriesAndItems(categoriesAndItemsFromChecklist);
  };

  // make sure that we don't insert unchecked items after checked and vice-versa
  const ensureOrderByChecked = (
    requestedIndex: number,
    item: Item,
    newCategory: Category,
  ) => {
    const indexOfFirstCheckedItem = newCategory.items
      .filter((_) => !_.checked)
      .filter((_) => _.id != item.id).length;

    const droppedAfterFirstCheckedItems =
      requestedIndex >= indexOfFirstCheckedItem;

    return item.checked !== droppedAfterFirstCheckedItems
      ? indexOfFirstCheckedItem
      : requestedIndex;
  };

  const moveItem = (listItems: Reorderable[], item: Item) => {
    const oldCategory = categories.find((category) =>
      category.items.some((_) => _.id === item.id),
    )!;
    const oldIndex = oldCategory.items.findIndex((_) => _.id === item.id);
    const itemIndex = listItems.findIndex((_) => _.id === item.id);
    const newCategory =
      listItems.slice(0, itemIndex).findLast(isCategory) ??
      listItems.find(isCategory) ??
      oldCategory;
    const newCategoryIndex = listItems.indexOf(newCategory);
    const requestedNewIndex = listItems
      .slice(newCategoryIndex + 1, itemIndex)
      .filter((_) => !isCategory(_)).length;
    const newIndex = ensureOrderByChecked(requestedNewIndex, item, newCategory);

    if (newCategory.id === oldCategory.id && oldIndex === newIndex) {
      return;
    }

    createChange({
      name: "moveItem",
      payload: {
        itemId: item.id,
        itemName: item.name,
        oldCategoryName: oldCategory.name,
        newCategoryName: newCategory.name,
        oldItemPosition: oldIndex + 1,
        newItemPosition: newIndex + 1,
      },
    });
  };

  const moveCategory = (listItems: Reorderable[], category: Category) => {
    const reorderedCategories = listItems.filter(isCategory);
    const oldIndex = categories.findIndex((_) => _.id === category.id);
    const newIndex = reorderedCategories.findIndex((_) => _.id === category.id);

    if (oldIndex === newIndex) {
      return;
    }

    createChange({
      name: "moveCategory",
      payload: {
        categoryName: category.name,
        oldPosition: oldIndex + 1,
        newPosition: newIndex + 1,
      },
    });
  };

  return (
    <AnimatePresence initial={false}>
      <Reorder.Group
        className="list-group list-group-flush"
        id="checklist-categories-and-items"
        values={categoriesAndItems}
        onReorder={onReorder}
      >
        {categoriesAndItems.map((listItem) => (
          <ReorderableListItem
            key={listItem.id}
            listItem={listItem}
            onDragStart={() => {
              draggedListItem.current = listItem;

              if (isCategory(listItem)) {
                setIsDraggingCategory(true);
                setCategoriesAndItems(categories);
              }
            }}
            onDragEnd={moveReordeable}
          >
            <ListItem
              listItem={listItem}
              authorization={checklist.authorization}
              is_template={checklist.is_template}
            ></ListItem>
          </ReorderableListItem>
        ))}
      </Reorder.Group>
    </AnimatePresence>
  );
};
